import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, getCategoryBySlug, getCategoryName } from '@/mocks/categories';
import { getArticlesByCategory, getHotArticles, getNewArticles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const articles = getArticlesByCategory(slug || '');
  const hotArticles = getHotArticles().slice(0, 3);
  const newArticles = getNewArticles().slice(0, 3);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 pb-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-12">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-100/70 text-primary-600">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-12">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1">
              <div className="mb-6">
                <div className="flex flex-wrap gap-2 mb-8">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                        cat.slug === slug
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-50 border border-background-200/60 text-foreground-600 hover:border-primary-200'
                      }`}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>

              {articles.length > 0 ? (
                <div className="space-y-4">
                  {articles.map((article) => (
                    <ArticleCard key={article.id} {...article} variant="horizontal" />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <p className="text-foreground-600">该分类暂无文章，敬请期待</p>
                </div>
              )}
            </div>

            <aside className="w-full lg:w-80 flex-shrink-0">
              <div className="space-y-8 lg:sticky lg:top-24">
                {hotArticles.length > 0 && (
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                      <i className="ri-fire-line text-secondary-500"></i>
                      热门文章
                    </h3>
                    <div className="space-y-1">
                      {hotArticles.map((a) => (
                        <ArticleCard key={a.id} {...a} variant="compact" />
                      ))}
                    </div>
                  </div>
                )}

                {newArticles.length > 0 && (
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                      <i className="ri-sparkling-line text-accent-500"></i>
                      最新发布
                    </h3>
                    <div className="space-y-1">
                      {newArticles.map((a) => (
                        <ArticleCard key={a.id} {...a} variant="compact" />
                      ))}
                    </div>
                  </div>
                )}

                <div className="p-5 rounded-xl bg-secondary-50 border border-background-200/40">
                  <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-2">
                    需要帮助？
                  </h4>
                  <p className="text-xs text-foreground-600 mb-3">
                    如果你有婚姻经营方面的困惑，欢迎联系我们
                  </p>
                  <Link
                    to="/about"
                    className="text-xs font-medium text-primary-500 hover:text-primary-600 transition-colors"
                  >
                    联系我们 →
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name} - 第一次人妻完全指南`,
            description: category.description,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://firstwife.guide/' },
                { '@type': 'ListItem', position: 2, name: category.name },
              ],
            },
          }),
        }}
      />
    </>
  );
}